import React, { useState } from 'react';
import {
  Save,
  Loader2,
  CheckCircle,
  RefreshCw,
  Info,
  Lock,
} from 'lucide-react';
import { useStudentsWithRattrapage, useSaveRattrapageGrades,
  useFinalizeRattrapageGrades } from '../../hooks/useProfessor';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';

interface RattrapagesTabProps {
  groupeId: string;
  moduleElementId: string;
  academicYearId?: string;
  readOnly?: boolean;
}

// Track grade edits
type GradeEdits = Record<string, { rattrapageGrade: number; comments?: string }>;

// Track raw input values for decimal support
type RawInputValues = Record<string, string>;

const RattrapagesTab: React.FC<RattrapagesTabProps> = ({
  groupeId,
  moduleElementId,
  academicYearId,
  readOnly = false,
}) => {
  const [gradeEdits, setGradeEdits] = useState<GradeEdits>({});
  const [rawInputs, setRawInputs] = useState<RawInputValues>({});
  const toast = useToast();

  // Fetch data
  const { data: rattrapageData, isLoading, refetch } = useStudentsWithRattrapage(
    groupeId,
    moduleElementId,
    academicYearId
  );

  const studentsWithRattrapage = rattrapageData?.students || [];
  // null = no cap configured in academic parameters (backend applies none)
  const rattrapageGradeCap = rattrapageData?.rattrapageGradeCap ?? null;
  // Same contract as regular grades: finalized rattrapages are read-only.
  const isFinalized = (rattrapageData?.isFinalized ?? false) || readOnly;
  const canFinalize = rattrapageData?.canFinalize ?? false;
  const finalizeMutation = useFinalizeRattrapageGrades();

  const handleFinalize = async () => {
    if (Object.keys(gradeEdits).length > 0) {
      toast.error(
        'Modifications non enregistrées',
        'Enregistrez vos saisies avant de finaliser les rattrapages.'
      );
      return;
    }
    if (!window.confirm(
      'Finaliser les notes de rattrapage ?\n\nCette action est irréversible : les notes ne pourront plus être modifiées.'
    )) {
      return;
    }
    try {
      const result = await finalizeMutation.mutateAsync({ groupeId, moduleElementId });
      await refetch();
      toast.success(
        'Rattrapages finalisés',
        `${result.finalizedCount} note(s) verrouillée(s).`
      );
    } catch (error) {
      toast.error(
        'Finalisation impossible',
        error instanceof Error ? error.message : 'Veuillez réessayer.'
      );
    }
  };

  // Mutations
  const saveGradesMutation = useSaveRattrapageGrades();

  // Handle grade change - supports comma and dot as decimal separator
  const handleGradeChange = (studentId: string, value: string) => {
    // Normalize: replace comma with dot
    const normalizedValue = value.replace(',', '.');

    // Allow clearing
    if (normalizedValue === '') {
      setRawInputs((prev) => {
        const updated = { ...prev };
        delete updated[studentId];
        return updated;
      });
      setGradeEdits((prev) => {
        const updated = { ...prev };
        delete updated[studentId];
        return updated;
      });
      return;
    }

    // Validate input
    const validPattern = /^-?\d*\.?\d*$/;
    if (!validPattern.test(normalizedValue)) return;

    // Store raw input
    setRawInputs((prev) => ({
      ...prev,
      [studentId]: normalizedValue,
    }));

    // Parse number
    const numValue = parseFloat(normalizedValue);

    // Update grade edits if valid
    if (!isNaN(numValue)) {
      setGradeEdits((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          rattrapageGrade: numValue,
        },
      }));
    }
  };

  // Handle comments change
  const handleCommentsChange = (studentId: string, comments: string) => {
    setGradeEdits((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        rattrapageGrade: prev[studentId]?.rattrapageGrade ?? 0,
        comments,
      },
    }));
  };

  // Save grades
  const handleSaveGrades = async () => {
    const grades = Object.entries(gradeEdits).map(([studentId, data]) => ({
      studentId,
      rattrapageGrade: data.rattrapageGrade,
      comments: data.comments,
    }));

    if (grades.length === 0) return;

    try {
      const result = await saveGradesMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto: { grades },
      });
      setGradeEdits({});
      setRawInputs({});
      await refetch();

      toast.success(
        'Notes enregistrées',
        `${result.savedCount} note(s) de rattrapage enregistrée(s). Toutes les composantes ont été mises à jour proportionnellement.`
      );
    } catch (error: any) {
      console.error('Failed to save rattrapage grades:', error);
      toast.error(
        'Échec de l\'enregistrement',
        error?.message || 'Veuillez réessayer.'
      );
    }
  };

  // Render status badge
  const renderStatusBadge = (status: 'PENDING' | 'GRADED') => {
    if (status === 'GRADED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle className="h-3 w-3" />
          Noté
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
        <RefreshCw className="h-3 w-3" />
        En attente
      </span>
    );
  };

  // Render reason badge
  const renderReasonBadge = (reason: string) => {
    let bgColor = 'bg-gray-100 text-gray-700';
    let text = reason;

    switch (reason) {
      case 'FAILED_GRADE':
        bgColor = 'bg-red-100 text-red-700';
        text = 'Note insuffisante';
        break;
      case 'EXCESSIVE_ABSENCE':
        bgColor = 'bg-orange-100 text-orange-700';
        text = 'Absences excessives';
        break;
      case 'FAILED_COMPONENT':
        bgColor = 'bg-amber-100 text-amber-700';
        text = 'Composante échouée';
        break;
      case 'MANUAL':
        bgColor = 'bg-blue-100 text-blue-700';
        text = 'Manuel';
        break;
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}>
        {text}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (studentsWithRattrapage.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
        <div className="text-center">
          <Info className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Aucun étudiant en rattrapage</p>
          <p className="text-sm text-gray-400 mt-1">
            Les étudiants ayant besoin de rattrapage apparaîtront ici
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-800 text-sm">Notes de rattrapage</h3>
            <p className="text-sm text-blue-700 mt-1">
              {rattrapageGradeCap !== null
                ? <>Les notes de rattrapage sont plafonnées à <strong>{rattrapageGradeCap}/20</strong> selon les paramètres académiques. </>
                : <>Aucun plafond n'est configuré pour les notes de rattrapage. </>}
              La note de rattrapage <strong>remplace la note globale</strong> : toutes les composantes seront mises à jour
              proportionnellement pour atteindre la note de rattrapage, avec traçabilité.
              La "Note plafonnée" sera calculée automatiquement et affichera un aperçu pendant la saisie.
            </p>
          </div>
        </div>
      </div>

      {/* Grade entry table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Étudiants en rattrapage</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Étudiant
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Raison
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Note originale
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Note rattrapage
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Note plafonnée
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Statut
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Commentaires
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {studentsWithRattrapage.map((student) => {
                const editedValue = gradeEdits[student.studentId];
                const rawInput = rawInputs[student.studentId];
                const displayValue = rawInput !== undefined
                  ? rawInput
                  : (editedValue?.rattrapageGrade !== undefined
                      ? editedValue.rattrapageGrade.toString()
                      : (student.rattrapageGrade !== null ? student.rattrapageGrade.toString() : ''));
                const hasEdit = editedValue !== undefined;

                // Calculate capped grade preview
                let cappedPreview = student.cappedGrade;
                if (editedValue?.rattrapageGrade !== undefined && !isNaN(editedValue.rattrapageGrade)) {
                  cappedPreview = rattrapageGradeCap !== null
                    ? Math.min(editedValue.rattrapageGrade, rattrapageGradeCap)
                    : editedValue.rattrapageGrade;
                }

                return (
                  <tr key={student.studentId} className="hover:bg-gray-50">
                    {/* Student */}
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {student.lastName} {student.firstName}
                        </div>
                        <div className="text-sm text-gray-500">{student.studentCode}</div>
                      </div>
                    </td>

                    {/* Reason */}
                    <td className="px-4 py-3 text-center">
                      {renderReasonBadge(student.reason)}
                    </td>

                    {/* Original Grade */}
                    <td className="px-4 py-3 text-center">
                      <span className={`font-medium ${
                        student.originalGrade !== null && student.originalGrade < 10
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }`}>
                        {student.originalGrade !== null ? student.originalGrade.toFixed(2) : '--'}
                      </span>
                    </td>

                    {/* Rattrapage Grade Input */}
                    <td className="px-4 py-3 text-center">
                      {!isFinalized ? (
                        <input
                          type="text"
                          inputMode="decimal"
                          value={displayValue}
                          onChange={(e) => handleGradeChange(student.studentId, e.target.value)}
                          className={`w-20 px-2 py-1 border rounded text-center text-sm ${
                            hasEdit
                              ? 'border-blue-400 bg-blue-50'
                              : 'border-gray-300'
                          }`}
                          placeholder="--"
                        />
                      ) : (
                        <span className="font-medium text-gray-900">
                          {student.rattrapageGrade !== null ? student.rattrapageGrade.toFixed(2) : '--'}
                        </span>
                      )}
                    </td>

                    {/* Capped Grade */}
                    <td className="px-4 py-3 text-center">
                      {cappedPreview !== null && cappedPreview !== undefined ? (
                        <span className={`font-medium ${
                          cappedPreview >= 10
                            ? 'text-green-600'
                            : 'text-red-600'
                        } ${hasEdit ? 'bg-blue-50 px-2 py-1 rounded' : ''}`}>
                          {cappedPreview.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-gray-400">--</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 text-center">
                      {renderStatusBadge(student.status)}
                    </td>

                    {/* Comments */}
                    <td className="px-4 py-3">
                      {!isFinalized ? (
                        <input
                          type="text"
                          value={editedValue?.comments ?? student.comments ?? ''}
                          onChange={(e) => handleCommentsChange(student.studentId, e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Optionnel"
                        />
                      ) : (
                        <span className="text-sm text-gray-600">{student.comments || '--'}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between gap-4">
          <div className="text-sm text-gray-500">
            {isFinalized ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium">
                <Lock className="h-4 w-4" />
                Rattrapages finalisés — notes verrouillées
              </span>
            ) : (
              Object.keys(gradeEdits).length > 0 && (
                <span className="text-blue-600">
                  {Object.keys(gradeEdits).length} modification(s) non enregistrée(s)
                </span>
              )
            )}
          </div>
          {!isFinalized && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveGrades}
                disabled={Object.keys(gradeEdits).length === 0 || saveGradesMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saveGradesMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Enregistrer
              </button>
              <button
                onClick={handleFinalize}
                disabled={!canFinalize || finalizeMutation.isPending}
                title={
                  canFinalize
                    ? 'Verrouiller définitivement les notes de rattrapage'
                    : 'Toutes les notes de rattrapage doivent être saisies avant de finaliser'
                }
                className="flex items-center gap-2 px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-50 transition-colors"
              >
                {finalizeMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Lock className="h-4 w-4" />
                )}
                Finaliser les rattrapages
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </div>
  );
};

export default RattrapagesTab;
